/*
  <article-toc data-target=".article__content" data-min-headings="2" data-min-words="600">
    Walks h2/h3 inside the target, assigns deterministic IDs (without
    overwriting existing ones), and renders a navigable list.

  Behaviour:
  - Skips render entirely if heading count < min-headings or
    target word count < min-words. The element remains in the DOM
    but renders no content.
  - Adds inline anchor link (<a class="heading-anchor">) to each heading.
  - Deduplicates IDs via -2, -3 suffixes on collisions.
  - Respects existing ID attributes if already set in the source markup.

  Designed to be additive — never throws if structure is missing.
*/

(() => {
  if (customElements.get('article-toc')) return;

  const SLUG_RE = /[^a-z0-9\s-]/g;

  function slugify(text) {
    return (text || '')
      .toLowerCase()
      .replace(SLUG_RE, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60) || 'section';
  }

  class ArticleTOC extends HTMLElement {
    connectedCallback() {
      const targetSelector = this.getAttribute('data-target') || '.article__content';
      const minHeadings = parseInt(this.getAttribute('data-min-headings') || '2', 10);
      const minWords = parseInt(this.getAttribute('data-min-words') || '600', 10);
      const heading = this.getAttribute('data-heading') || 'In this guide';

      const target = document.querySelector(targetSelector);
      if (!target) return;

      const wordCount = (target.innerText || '').trim().split(/\s+/).length;
      const headings = Array.from(target.querySelectorAll('h2, h3'));
      const h2Count = headings.filter((h) => h.tagName === 'H2').length;

      if (h2Count < minHeadings || wordCount < minWords) {
        // Below threshold: render nothing AND collapse the host element, else
        // its block display + vertical margin leaves an empty spacer.
        this.style.display = 'none';
        return;
      }

      const seen = new Map();
      const items = headings.map((h) => {
        const base = slugify(h.textContent);
        let id = h.id;
        if (id) {
          // Reserve the slug even for hand-authored ids, so a later
          // auto-generated heading with the same text doesn't collide
          // and produce two TOC links pointing at the same fragment.
          seen.set(base, (seen.get(base) || 0) + 1);
        } else {
          let candidate = base;
          let count = seen.get(base) || 0;
          if (count > 0) candidate = base + '-' + (count + 1);
          seen.set(base, count + 1);
          id = candidate;
          h.id = id;
        }
        // Append anchor link
        if (!h.querySelector('.heading-anchor')) {
          const a = document.createElement('a');
          a.className = 'heading-anchor';
          a.href = '#' + id;
          a.setAttribute('aria-label', 'Copy link to ' + h.textContent.trim());
          a.textContent = '#';
          h.appendChild(a);
        }
        return {
          id,
          text: (h.firstChild && h.firstChild.nodeValue ? h.firstChild.nodeValue : h.textContent).trim().replace(/#$/, '').trim(),
          level: h.tagName.toLowerCase(),
          el: h,
        };
      });

      // Build TOC markup
      const wrapper = document.createElement('div');
      wrapper.className = 'article-toc__panel';

      const list = document.createElement('ol');
      list.className = 'article-toc__list';

      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'article-toc__item article-toc__item--' + item.level;
        const link = document.createElement('a');
        link.className = 'article-toc__link';
        link.href = '#' + item.id;
        link.textContent = item.text;
        li.appendChild(link);
        list.appendChild(li);
      });

      wrapper.appendChild(list);

      // Heading. Mobile: collapsible button. Desktop: plain heading.
      const isMobile = window.matchMedia('(max-width: 1023.98px)').matches;
      if (isMobile) {
        this.classList.add('article-toc--collapsible');
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'article-toc__toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.innerHTML = `
          <span>${heading}</span>
          <svg class="article-toc__toggle-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M7.5 3.74121L13.75 9.99121L7.5 16.2412" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        wrapper.style.display = 'none';
        toggle.addEventListener('click', () => {
          const open = this.hasAttribute('open');
          if (open) {
            this.removeAttribute('open');
            toggle.setAttribute('aria-expanded', 'false');
            wrapper.style.display = 'none';
          } else {
            this.setAttribute('open', '');
            toggle.setAttribute('aria-expanded', 'true');
            wrapper.style.display = '';
          }
        });
        this.appendChild(toggle);
        this.appendChild(wrapper);
      } else {
        const h = document.createElement('h2');
        h.className = 'article-toc__heading';
        h.textContent = heading;
        this.appendChild(h);
        this.appendChild(wrapper);
      }

      // Active-section tracking via IntersectionObserver
      try {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const id = entry.target.id;
              const links = list.querySelectorAll('.article-toc__link');
              links.forEach((link) => {
                if (link.getAttribute('href') === '#' + id) {
                  link.setAttribute('aria-current', 'true');
                } else {
                  link.removeAttribute('aria-current');
                }
              });
            });
          },
          { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
        );
        items.forEach((item) => observer.observe(item.el));
      } catch (_) {
        // IntersectionObserver unavailable; degrade quietly.
      }
    }
  }

  customElements.define('article-toc', ArticleTOC);
})();
